import { constants } from "@qgiv/core-js";

/**
 * @typedef {import("@qgiv/donation-form").ReduxTypes.Receipts.Receipt} Receipt
 * @typedef {import("@qgiv/donation-form").ReduxTypes.Receipts.TransactionField} TransactionField
 */

// =============================================================================
// MARK: CONSTANTS
// =============================================================================

const {
    ENUMS: { FieldType },
} = constants;

const CHECKBOX_YES_VALUE = "yes";

// =============================================================================
// MARK: FIELD TYPE PREDICATES
// =============================================================================

/**
 * @param {string|number} type
 * @returns {boolean}
 */
const isCheckboxField = (type) =>
    Number(type) === FieldType.CHECKBOX || type === FieldType.OLD_CHECKBOX;

/**
 * @param {string|number} type
 * @returns {boolean}
 */
const isMultipleSelectionField = (type) =>
    Number(type) === FieldType.MULTIPLE_SELECTION;

// =============================================================================
// MARK: FIELD VALIDATION
// =============================================================================

/**
 * @param {TransactionField} field
 * @param {number} fieldContext
 * @returns {boolean}
 */
const hasCorrectContext = (field, fieldContext) =>
    Number(field.context) === fieldContext;

/**
 * @param {TransactionField} field
 * @returns {boolean}
 */
const hasValidPropertiesAndData = (field) => {
    if (!field) return false;

    const { data, fld, ref, type } = field;
    const hasRequiredProperties = Boolean(fld && ref && type);
    const hasPopulatedData = typeof data === "string" && data.trim().length > 0;
    const isValid = hasRequiredProperties && hasPopulatedData;

    return isValid;
};

/**
 * @param {TransactionField} field
 * @param {number} fieldContext
 * @returns {boolean}
 */
const isValidFieldByContext = (field, fieldContext) => {
    if (
        !hasValidPropertiesAndData(field) ||
        !hasCorrectContext(field, fieldContext)
    ) {
        return false;
    }

    if (isCheckboxField(field.type)) {
        // Do not include unchecked checkboxes in the data layer
        const isChecked = field.data.toLowerCase() === CHECKBOX_YES_VALUE;
        return isChecked;
    }

    return true;
};

// =============================================================================
// MARK: FIELD VALUE PROCESSING
// =============================================================================

/**
 * @param {TransactionField[]} fieldCollection
 * @returns {string|boolean|string[]}
 */
const getFieldValue = (fieldCollection) => {
    const firstField = fieldCollection[0];

    if (isMultipleSelectionField(firstField.type)) {
        const multipleSelectionValues = fieldCollection.map(
            (field) => field.data,
        );
        return multipleSelectionValues;
    }

    if (isCheckboxField(firstField.type)) {
        // Unchecked checkboxes are filtered out in isValidFieldByContext
        return true;
    }

    return firstField.data;
};

// =============================================================================
// MARK: COLLECTION TRANSFORMERS
// =============================================================================

/**
 * @param {TransactionField[]} fields
 * @returns {Record<string, TransactionField[]>}
 */
export const groupFieldsById = (fields) => {
    const groupedFields = fields.reduce((acc, field) => {
        const groups = { ...acc };
        const id = field.fld;
        groups[id] = groups[id] || [];
        groups[id].push(field);
        return groups;
    }, {});

    return groupedFields;
};

/**
 * @param {Record<string, TransactionField[]>} validFieldsById
 * @returns {Record<string, string|boolean|string[]>}
 */
export const getFieldsOptions = (validFieldsById) => {
    const fieldsOptions = Object.values(validFieldsById).reduce(
        (acc, fieldCollection) => {
            const reportingLabel = fieldCollection[0].ref;
            const fieldValue = getFieldValue(fieldCollection);
            const updatedAccumulator = {
                ...acc,
                [reportingLabel]: fieldValue,
            };
            return updatedAccumulator;
        },
        {},
    );

    return fieldsOptions;
};

// =============================================================================
// MARK: RECEIPT FIELD EXTRACTORS
// =============================================================================

/**
 * @param {Receipt & { transaction: NonNullable<Receipt['transaction']>} } receipt
 * @returns {TransactionField[]}
 */
export const getCustomFieldsFromReceipt = (receipt) => {
    const { fields } = receipt.transaction;
    const hasFields = Array.isArray(fields) && fields.length > 0;
    const customFieldsContext = FieldType.QUESTION;
    let customFieldsFromReceipt = [];

    if (hasFields) {
        customFieldsFromReceipt = fields.filter((field) =>
            isValidFieldByContext(field, customFieldsContext),
        );
    }
    return customFieldsFromReceipt;
};
